import { useCallback, useMemo, useState } from 'react';
import { useActionLock } from '../../../../../hooks/useActionLock';
import { normalizeFixed, toNumberSafe, resolvePedidoByFuncao, parseNumberFromApi } from '../../../../../helpers/helpers';

import { useMRTListController } from '../../../../../hooks/useMRTController';

import { handleParceiroLookup, handleColaboradorLookup } from '../shared/pessoaLookup';

const { v4: uuidv4 } = require('uuid');

const formatarValorMonetario = (valor) => {
    if (valor === null || valor === undefined || valor === '') return '';

    const numero = Number(valor);

    if (!Number.isFinite(numero)) return '';

    return numero.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

/**
 * useItensModule
 *
 * Contrato novo:
 * - key
 * - main
 * - draft
 * - list
 * - mrt
 * - actions
 * - services
 * - policies
 *
 * Compat temporária:
 * - state
 * - actions (mantidos)
 */
export function useItensModule(params) {
    const {
        dados,
        setDados,

        // parâmetros
        profile,
        parametro,
        operacao,
        operacaoParametros,
        natureza,
        empresaIds,
        casaDecimal = 2,

        // APIs
        ProdutoAPI,
        EmbalagemAPI,
        TabelaAPI,
        TabelaPrecoAPI,
        MovimentoAPI,
        DataListAPI,

        // infra UI
        setFlashMessage,

        // formatadores
        formatarNumeroDecimal,
        formatarNumeroDecimal3Digitos,

        // futuras policies
        policies = {},

        // futuro MRT
        mrt = null,
        dataPlantao,
        indexadores,

        changeParceiroAsync,
        changeColaboradorAsync,
        consultaDatalist,
        consultaDatalistColaborador,
        //variáveis
        modoTabelaPreco,
    } = params;

    const utiliza3digitos = !!operacaoParametros?.utiliza3digitos;

    const produtoFiltro = {
        compra: operacaoParametros?.produto_compra ?? false,
        venda: operacaoParametros?.produto_venda ?? false,
        locavel: operacaoParametros?.produto_locavel ?? false,
        industria: operacaoParametros?.produto_industria ?? false,
        materiaPrima: operacaoParametros?.produto_materiaPrima ?? false,
        imovel: operacaoParametros?.produto_imovel ?? false,
        vtr: operacaoParametros?.produto_vtr ?? false,
        bem: operacaoParametros?.produto_bem ?? false,
    };

    //datalists
    const [parceiroItem, setParceiroItem] = useState({});
    const [optionsParceiroItem, setOptionsParceiroItem] = useState([]);
    const [colaboradorItem, setColaboradorItem] = useState({});
    const [optionsColaboradorItem, setOptionsColaboradorItem] = useState([]);

    // =========================================================
    // MAIN STATE
    // =========================================================
    const mainState = dados;
    const setMainState = setDados;

    // =========================================================
    // LIST STATE
    // =========================================================
    const [itensProdutos, setItensProdutos] = useState([]);
    const [rowCountProduto, setRowCountProduto] = useState(0);

    const [Produto, setProduto] = useState({});
    const [Embalagem, setEmbalagem] = useState({});

    const [OptionsProdutos, setOptionsProdutos] = useState([]);
    const [OptionsEmbalagem, setOptionsEmbalagem] = useState([]);

    const [viewItensProdutos, setViewItensProdutos] = useState(null);

    // flags de grid (preparação para MRT local do módulo)

    const [isLoading, setIsLoading] = useState(false);
    const [isRefetching, setIsRefetching] = useState(false);
    const [isError, setIsError] = useState(false);
    const saveLock = useActionLock();
    const refreshLock = useActionLock();
    const costRecalculationLock = useActionLock();
    const flags = {
        isSaving: saveLock.isLocked,

        isRefreshing: refreshLock.isLocked,

        isRecalculatingCosts: costRecalculationLock.isLocked,

        isLoading,
        isRefetching,
        isError,
    };

    // =========================================================
    // DRAFT STATE
    // =========================================================
    const [draftValue, setDraftValue] = useState({
        id: null,
        uuid: null,

        uuidTabela: null,
        embalagemIdTabela: null,

        movimentoId: dados?.id ?? null,
        empresaId: dados?.empresaId ?? null,
        tabelaPrecoId: null,
        produtoId: null,
        embalagemId: null,
        situacaoId: null,
        almoxarifadoId: dados?.almoxarifadoId ?? null,

        fator: 1,

        quantidade: Number(profile?.parametros?.itens?.quantidadePadrao).toFixed(casaDecimal),
        quantidadeOriginal: '',
        quantidadeFalta: '',
        quantidadeCorte: '',

        precoNegociado: '0.00',
        precoPorUnidade: '0.00',

        precoUnitario: '0.00',
        preco: '0.00',

        precoBase: '0.00',

        valorUnitario: '0.00',
        descontoPercentual: '0.00',
        descontoValor: '0.00',
        valorTotal: '0.00',

        custo: '0.00',
        custoCalculado: '0.00',

        pesoBruto: '0.00',
        pesoLiquido: '0.00',
        volume: 0,

        tributacaoId: null,
        ncmId: null,

        pedido: false,
        ativo: true,

        conferencia: operacao?.conferencia,
        natureza: natureza,

        Produto: {},
        Embalagem: {},
        Empresa: {},
        Situacao: {},
        tabelaPreco: {},

        // indústria
        itemVinculadoId: null,
        refugo: false,
        refugoGerouFinanceiro: false,
        pessoaId: null,
        conferenciaCodigo: null,
        parceiroItem: {},
        colaboradorItem: {},
        colaboradorId: null,

        // logística
        volumes: 0,
        volumesTotal: 0,
        dataConferencia: null,

        funcao: 'Adicionar',
        index: null,
    });

    // aliases temporários para compat
    const draft = draftValue;
    const setDraft = setDraftValue;

    // =========================================================
    // SERVICES
    // =========================================================
    const mergeDadosMovimentoItems = useCallback(
        (lista) => {
            setDados((prev) => ({
                ...prev,
                movimentoItems: Array.isArray(lista) ? lista : [],
            }));
        },
        [setDados]
    );

    const commitList = useCallback(
        (lista) => {
            const safe = Array.isArray(lista) ? lista : [];
            setItensProdutos(safe);
            setRowCountProduto(safe.length);
            mergeDadosMovimentoItems(safe);
        },
        [mergeDadosMovimentoItems]
    );

    const normalizeDraftToItem = useCallback(
        (d) => {
            const empresaId = d.empresaId || dados?.empresaId || null;
            const movimentoId = d.movimentoId || dados?.id || null;
            const almoxarifadoId = d.almoxarifadoId || dados?.almoxarifadoId || null;

            const qtd = d.quantidade !== '' && d.quantidade != null ? Number(d.quantidade) : null;

            return {
                ...d,
                empresaId,
                movimentoId,
                almoxarifadoId,

                conferencia: operacao?.conferencia,
                natureza: natureza,

                fator: toNumberSafe(d.fator) || 1,

                quantidade:
                    qtd == null
                        ? Number(profile?.parametros?.itens?.quantidadePadrao).toFixed(casaDecimal)
                        : Number(qtd).toFixed(casaDecimal),

                quantidadeOriginal:
                    d.quantidadeOriginal !== '' && d.quantidadeOriginal != null
                        ? Number(d.quantidadeOriginal).toFixed(casaDecimal)
                        : (d.quantidadeOriginal ?? Number(profile?.parametros?.itens?.quantidadePadrao).toFixed(casaDecimal)),

                quantidadeFalta:
                    d.quantidadeFalta !== '' && d.quantidadeFalta != null
                        ? Number(d.quantidadeFalta).toFixed(casaDecimal)
                        : (d.quantidadeFalta ?? ''),

                quantidadeCorte:
                    d.quantidadeCorte !== '' && d.quantidadeCorte != null
                        ? Number(d.quantidadeCorte).toFixed(casaDecimal)
                        : (d.quantidadeCorte ?? ''),

                precoNegociado: normalizeFixed(d.precoNegociado, 2),
                precoPorUnidade: normalizeFixed(d.precoPorUnidade, 2),

                precoUnitario: normalizeFixed(d.precoUnitario, 2),
                preco: normalizeFixed(d.preco, 2),
                precoBase: normalizeFixed(d.precoBase, 2),

                valorUnitario: normalizeFixed(d.valorUnitario, 2),
                descontoPercentual: normalizeFixed(d.descontoPercentual, 2),
                descontoValor: normalizeFixed(d.descontoValor, 2),
                valorTotal: normalizeFixed(d.valorTotal, 2),

                custo: normalizeFixed(d.custo || d.precoNegociado, 2),
                custoCalculado: normalizeFixed(d.custoCalculado || d.precoNegociado, 2),

                pesoBruto: normalizeFixed(d.pesoBruto, 2),
                pesoLiquido: normalizeFixed(d.pesoLiquido, 2),
                volume: toNumberSafe(d.volume),

                Produto: d.Produto || Produto || {},
                Embalagem: d.Embalagem || Embalagem || {},
                Pessoa: d.parceiroItem || parceiroItem || {},
                Colaborador: d.colaboradorItem || colaboradorItem || {},
            };
        },
        [
            dados?.empresaId,
            dados?.id,
            dados?.almoxarifadoId,
            operacao?.conferencia,
            natureza,
            casaDecimal,
            Produto,
            Embalagem,
            profile,
        ]
    );

    const getKeyValue = useCallback(
        (obj) => {
            if (!obj) return null;

            const produtoId = obj.produtoId ?? obj?.Produto?.id ?? null;

            if (operacaoParametros?.formTabelaOrProduto) {
                return obj.uuidTabela || produtoId || obj.uuid || obj.id || null;
            }

            return obj.uuid || obj.id || produtoId || null;
        },
        [operacaoParametros?.formTabelaOrProduto]
    );

    const upsertLocal = useCallback(
        (lista, d) => {
            const safe = Array.isArray(lista) ? lista : [];
            const isCancelar = (d?.funcao || '')?.toLowerCase() === 'cancelar';

            if (isCancelar) {
                const key = getKeyValue(d);
                if (!key) return safe;
                return safe.filter((it) => getKeyValue(it) !== key);
            }

            const draftToUse = isCancelar
                ? {
                      ...d,
                      pedido: false,
                      funcao: 'Adicionar',
                      quantidade: Number(profile?.parametros?.itens?.quantidadePadrao).toFixed(casaDecimal),
                      quantidadeOriginal: 0,
                      quantidadeCorte: 0,
                      quantidadeFalta: 0,
                      descontoPercentual: '0.00',
                      descontoValor: '0.00',
                      valorTotal: '0.00',
                      valorUnitario: '0.00',
                      precoNegociado: '0.00',
                      precoPorUnidade: '0.00',
                  }
                : d;

            const keyDraft = getKeyValue(draftToUse);
            if (!keyDraft) return safe;

            const normalized = normalizeDraftToItem(draftToUse);
            const idx = safe.findIndex((it) => getKeyValue(it) === keyDraft);

            if (idx === -1) {
                const withUuid = { ...normalized, uuid: normalized.uuid || uuidv4() };
                return [...safe, withUuid];
            }

            const copy = [...safe];
            copy[idx] = {
                ...copy[idx],
                ...normalized,
                uuidTabela: copy[idx].uuidTabela ?? normalized.uuidTabela,
                embalagemIdTabela: copy[idx].embalagemIdTabela ?? normalized.embalagemIdTabela,
                Produto: normalized.Produto || copy[idx].Produto,
                Embalagem: normalized.Embalagem || copy[idx].Embalagem,
                Empresa: normalized.Empresa || copy[idx].Empresa,
            };

            return copy;
        },
        [normalizeDraftToItem, getKeyValue, profile, casaDecimal]
    );

    async function embalagensDoProduto({ empresaIds, produtoId }) {
        const empresaId = (Array.isArray(empresaIds) && empresaIds[0]) || null;
        if (!empresaId || !produtoId) return [];

        try {
            const res = await MovimentoAPI.retornaEmbalagensByProdutoId(empresaId, produtoId);

            const raw = Array.isArray(res)
                ? res
                : Array.isArray(res?.data)
                  ? res.data
                  : Array.isArray(res?.data?.data)
                    ? res.data.data
                    : [];

            const embalagens = raw.map((item) => item?.Embalagem).filter(Boolean);

            const map = new Map();
            for (const emb of embalagens) {
                if (emb?.id != null) map.set(Number(emb.id), emb);
            }

            const unique = Array.from(map.values());
            unique.sort((a, b) => (a?.descricao || '').localeCompare(b?.descricao || '', 'pt-BR'));

            setOptionsEmbalagem(unique);
            return unique;
        } catch (error) {
            console.warn('Erro ao buscar embalagens do produto:', error);
            return [];
        }
    }

    async function produtoByIdOrUuid({ id, uuid }) {
        if (!id && !uuid) return null;
        try {
            return await ProdutoAPI.obterProdutoByIdOrUUID({ id, uuid });
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    const consultaDatalistProduto = async ({ empresaId, tabelaPrecoId, filtro, filtraPorDiaHora, operacaoParametros }) => {
        if (!tabelaPrecoId) {
            setFlashMessage('Selecione uma tabela de preço!', 'warning');
            return [];
        }

        return await DataListAPI.dataListTabelaPrecoItemProduto({
            empresaId,
            tabelaPrecoId,
            filtro,
            filtraPorDiaHora,
            ...(filtraPorDiaHora
                ? {
                      utilizaRegraHoraMax: true,
                      dataMovimento: dados?.data || null,
                      horaMaxCafe: parametro?.horaMaxCafe,
                      horaMaxAlmoco: parametro?.horaMaxAlmoco,
                      horaMaxJanta: parametro?.horaMaxJanta,
                  }
                : {}),
        });
    };

    const consultaEmbalagemByProdutoId = async (empresaId, produtoId) => {
        if (!produtoId) return [];
        const result = await MovimentoAPI.retornaEmbalagensByProdutoId(empresaId, produtoId);

        if (Array.isArray(result) && result.length === 0) {
            setFlashMessage('Nenhuma embalagem encontrada para o produto.', 'error');
        } else if (Array.isArray(result) && result.length > 0) {
            const embalagens = result.map((item) => item.Embalagem);
            setOptionsEmbalagem(embalagens);
            if (draft.funcao?.toLowerCase() === 'adicionar') {
                setDraft((prev) => ({ ...prev, embalagemId: embalagens[0].id }));
            }
        }

        return result;
    };

    const precoByEmbalagemId = async (obj) => {
        return await TabelaPrecoAPI.obterPrecoByEmbalagemId(obj);
    };

    const aplicarPrecoIndexado = useCallback(
        (produtoBase, flags) => {
            if (!produtoBase) return produtoBase;

            // XML já vem com vProd / vUnCom / qCom.
            // Não recalcula valor por fator.
            if (produtoBase.importadoXml) {
                return {
                    ...produtoBase,
                    valorTotal: Number(produtoBase.valorTotal ?? 0).toFixed(2),
                    valorUnitario: Number(produtoBase.vUnCom ?? produtoBase.valorUnitario ?? 0).toFixed(2),
                    preco: Number(produtoBase.vUnCom ?? produtoBase.preco ?? produtoBase.valorUnitario ?? 0).toFixed(2),

                    precoUnitario: Number(
                        produtoBase.vUnCom ?? produtoBase.precoUnitario ?? produtoBase.valorUnitario ?? 0
                    ).toFixed(2),

                    precoNegociado: Number(produtoBase.vUnCom ?? produtoBase.preco ?? 0).toFixed(2),
                    precoPorUnidade: Number(produtoBase.vUnCom ?? produtoBase.valorUnitario ?? 0).toFixed(2),

                    ignoraRegraPrecoDesconto: true,
                };
            }

            const { custo, custoFilial } = flags || {};
            const quantidade = Number(produtoBase.quantidade || 0);
            const fator = Number(produtoBase.fator || 1);

            const precoBase =
                produtoBase.precoBase != null
                    ? Number(produtoBase.precoBase)
                    : Number(produtoBase.preco || produtoBase.valorUnitario || 0); // remover precoUnitario futuro

            let precoNegociado = precoBase;
            let preco = precoBase;
            let aplicouIndexador = false;

            if ((custo || custoFilial) && produtoBase.prato && Array.isArray(indexadores) && indexadores.length > 0) {
                const indexadorPratoVigente = indexadores.find(
                    (idx) => idx.prato === true && idx.vigente === true && idx.ativo === true
                );

                const valorIndexador = Number(indexadorPratoVigente?.valor || 0);

                if (valorIndexador > 0) {
                    preco = valorIndexador;
                    precoNegociado = valorIndexador;
                    aplicouIndexador = true;
                }
            }

            const totalItem = quantidade * preco;

            return {
                ...produtoBase,
                precoBase,
                preco,
                custo: preco,
                custoCalculado: preco,
                precoUnitario: preco,
                valorUnitario: Number(precoNegociado).toFixed(2),
                precoNegociado: Number(precoNegociado).toFixed(2),
                precoPorUnidade: Number(precoNegociado / (fator * quantidade)).toFixed(2),
                valorTotal: totalItem.toFixed(2),
                aplicouIndexador,
                permitePrecoAbaixoTabela: aplicouIndexador,
                ignoraRegraPrecoDesconto: aplicouIndexador,
            };
        },
        [indexadores]
    );

    const obterMovimentoItem = async ({ refugo, conferenciaCodigo, empresaId, operacaoIds }) => {
        if (!conferenciaCodigo || refugo) return;

        try {
            const data = await MovimentoAPI.obterMovimentoItemByConferenciaCodigo({
                refugo,
                conferenciaCodigo,
                empresaId,
                operacaoIds,
            });

            if (!data) return;

            const item = data?.movimentoItems?.[0];
            const produto = item?.Produto;
            const pessoa = item?.Pessoa;

            const produtoId = item?.produtoId;
            const quantidade = Number(item?.quantidade).toFixed(casaDecimal);
            const fator = Number(item?.fator);

            const volume = Number(produto.altura * produto.comprimento * produto.largura * quantidade);
            const pesoBruto = Number(produto?.pesoBruto * quantidade ?? 0);
            const pesoLiquido = Number(produto?.pesoLiquido * quantidade ?? 0);
            const pesoMolecular = Number(produto?.pesoMolecular * quantidade ?? 0);

            await consultaEmbalagemByProdutoId(dados.empresaId, produtoId);

            setDraft((prev) => ({
                ...prev,
                Produto: produto,
                produtoId,
                embalagemId: item?.embalagemId,
                quantidade: Number(item?.quantidade).toFixed(casaDecimal),
                quantidadeOriginal: Number(item?.quantidade).toFixed(casaDecimal),
                custo: Number(item?.custo),
                custoCalculado: Number(item?.custoCalculado ?? 0),
                precoNegociado: Number(item?.precoNegociado ?? 0),
                precoPorUnidade: Number(item?.precoPorUnidade ?? 0),
                valorUnitario: Number(item?.valorUnitario ?? 0),
                valorTotal: Number(item?.valorTotal ?? 0),
                tabelaPrecoId: item?.tabelaPrecoId,
                ncmId: item?.ncmId,
                volume,
                fator,
                pesoBruto,
                pesoLiquido,
                pesoMolecular,
                pessoaId: item?.pessoaId,
                itemVinculadoId: item?.id ?? null,
                precoBase: Number(item?.precoBase),
            }));

            setProduto(produto);
            setParceiroItem(pessoa);

            return data;
        } catch (error) {
            console.error(error);
        }
    };

    // =========================================================
    // ACTIONS
    // =========================================================
    const clear = useCallback(
        (opts = {}) => {
            const { keepTabelaPrecoId = true } = opts;

            setDraft((prev) => ({
                ...prev,
                id: null,
                uuid: null,
                movimentoId: dados?.id ?? null,
                empresaId: dados?.empresaId ?? null,
                tabelaPrecoId: keepTabelaPrecoId ? prev.tabelaPrecoId : null,
                uuidTabela: null,
                embalagemIdTabela: null,
                produtoId: null,
                embalagemId: null,
                situacaoId: null,
                quantidade: Number(profile?.parametros?.itens?.quantidadePadrao).toFixed(casaDecimal),
                quantidadeOriginal: '',
                quantidadeFalta: '',
                quantidadeCorte: '',
                precoUnitario: '0.00',
                preco: '0.00',
                precoBase: '0.00',
                valorUnitario: '0.00',
                precoNegociado: '0.00',
                precoPorUnidade: '0.00',
                descontoPercentual: '0.00',
                descontoValor: '0.00',
                valorTotal: '0.00',
                custo: '0.00',
                custoCalculado: '0.00',
                pesoBruto: '0.00',
                pesoLiquido: '0.00',
                volume: 0,
                tributacaoId: null,
                ncmId: null,
                pedido: false,
                ativo: true,
                conferencia: operacao?.conferencia,
                natureza: natureza,
                Produto: {},
                Embalagem: {},
                Situacao: {},
                volumes: 0,
                volumesTotal: 0,
                dataConferencia: null,
                itemVinculadoId: null,
                refugo: false,
                refugoGerouFinanceiro: false,
                pessoaId: null,
                conferenciaCodigo: null,
                parceiroItem: {},
                colaboradorItem: {},
                funcao: 'Adicionar',
                index: null,
            }));

            setProduto({});
            setEmbalagem({});
            setOptionsEmbalagem([]);
            setParceiroItem({});
            setOptionsParceiroItem([]);
            setColaboradorItem({});
            setOptionsColaboradorItem([]);
        },
        [dados?.id, dados?.empresaId, operacao?.conferencia, natureza, profile, casaDecimal]
    );

    const selectRow = useCallback(
        async (rowOriginal, rowIndex, funcao) => {
            const row = rowOriginal || {};

            const produtoId = row?.produtoId ?? row?.Produto?.id ?? null;

            const embalagemId = row?.embalagemId ?? row?.Embalagem?.id ?? null;

            const pedido = resolvePedidoByFuncao(funcao, row?.pedido);

            const qtdNum = parseNumberFromApi(row?.quantidade);

            const qtdStr =
                qtdNum == null
                    ? Number(profile?.parametros?.itens?.quantidadePadrao).toFixed(casaDecimal)
                    : qtdNum.toFixed(casaDecimal);

            const pessoa = row?.Pessoa ?? {};
            const colaborador = row?.Colaborador ?? {};

            /*
             * selectRow apenas carrega os valores existentes.
             *
             * A origem dos impostos — Nenhum, Proprio, XML
             * ou Origem — não deve ser interpretada aqui.
             */
            setDraft((prev) => ({
                ...prev,

                Embalagem: row?.Embalagem ?? {},
                Empresa: row?.Empresa ?? {},
                Produto: row?.Produto ?? {},
                Situacao: row?.Situacao ?? {},

                colaboradorItem: row?.Colaborador ?? {},

                parceiroItem: row?.Pessoa ?? {},

                tabelaPreco: row?.tabelaPreco ?? {},

                funcao: funcao || 'Adicionar',
                index: rowIndex,

                uuid: row?.uuid ?? prev?.uuid,

                id: row?.id ?? null,

                produtoId,
                embalagemId,

                situacaoId: row?.situacaoId ?? null,

                embalagemIdTabela: row?.embalagemIdTabela,

                uuidTabela: row?.uuidTabela,

                movimentoId: row?.movimentoId ?? dados?.id ?? prev?.movimentoId ?? null,

                empresaId: row?.empresaId ?? dados?.empresaId ?? prev?.empresaId ?? null,

                almoxarifadoId: row?.almoxarifadoId ?? dados?.almoxarifadoId ?? prev?.almoxarifadoId ?? null,

                tabelaPrecoId: row?.tabelaPrecoId ?? prev?.tabelaPrecoId,

                quantidade: qtdStr,

                fator: row?.fator ?? prev?.fator,

                precoBase: Number(row?.precoBase ?? row?.preco ?? 0).toFixed(2),

                preco: Number(row?.preco ?? row?.precoUnitario ?? 0).toFixed(2),

                precoNegociado: Number(row?.precoNegociado ?? 0).toFixed(2),

                precoPorUnidade: Number(row?.precoPorUnidade ?? 0).toFixed(2),

                precoUnitario: Number(row?.precoUnitario ?? 0).toFixed(2),

                valorUnitario: Number(row?.valorUnitario ?? prev?.valorUnitario ?? 0).toFixed(2),

                descontoPercentualItemTabela: Number(row?.descontoPercentualItemTabela ?? 0),

                descontoPercentual: Number(row?.descontoPercentual ?? prev?.descontoPercentual ?? 0).toFixed(2),

                descontoValor: Number(row?.descontoValor ?? prev?.descontoValor ?? 0).toFixed(2),

                descontoFinanceiro: Number(row?.descontoFinanceiro ?? prev?.descontoFinanceiro ?? 0).toFixed(2),

                valorTotal: Number(row?.valorTotal ?? prev?.valorTotal ?? 0).toFixed(2),

                volumes: row?.volumes ?? prev?.volumes,

                volumesTotal: row?.volumesTotal ?? prev?.volumesTotal,

                dataConferencia: row?.dataConferencia ?? null,

                itemVinculadoId: row?.itemVinculadoId ?? null,

                refugo: row?.refugo ?? false,

                refugoGerouFinanceiro: row?.refugoGerouFinanceiro ?? false,

                pessoaId: row?.pessoaId ?? null,

                colaboradorId: row?.colaboradorId ?? null,

                natureza,

                conferencia: operacao?.conferencia,

                prato: row?.prato,

                pedido,

                /*
                 * Impostos já gravados no item.
                 * Nenhum valor é recalculado pelo selectRow.
                 */
                tributacaoId: row?.tributacaoId ?? null,

                baseCalculo: Number(row?.baseCalculo ?? row?.vBC ?? 0),

                ipiAliquota: Number(row?.ipiAliquota ?? row?.pIPI ?? 0),

                ipi: Number(row?.ipi ?? row?.vIPI ?? 0),

                icmsAliquota: Number(row?.icmsAliquota ?? row?.pICMS ?? 0),

                icms: Number(row?.icms ?? row?.vICMS ?? 0),

                icmsST: Number(row?.icmsST ?? row?.vICMSST ?? 0),

                fecoepAliquota: Number(row?.fecoepAliquota ?? row?.pFCP ?? 0),

                fecoep: Number(row?.fecoep ?? row?.vFCP ?? 0),

                fecoepST: Number(row?.fecoepST ?? row?.vFCPST ?? 0),

                mva: Number(row?.mva ?? row?.pMVAST ?? 0),
            }));

            setDraft((prev) =>
                aplicarPrecoIndexado(prev, {
                    custo: !!dados?.custo,
                    custoFilial: !!dados?.custoFilial,
                })
            );

            setParceiroItem(pessoa);
            setColaboradorItem(colaborador);

            // 1) Produto visual imediato
            if (row?.Produto) {
                setProduto(row.Produto);
            } else if (produtoId) {
                try {
                    const produtoResp = await produtoByIdOrUuid({
                        id: produtoId,
                    });

                    const produtoObj = produtoResp?.data ?? produtoResp ?? {};

                    setProduto(produtoObj);
                } catch (error) {
                    console.error('Erro ao carregar produto do item:', error);

                    setProduto({});
                }
            } else {
                setProduto({});
            }

            // 2) Sem produto, limpa embalagem
            if (!produtoId) {
                setEmbalagem({});
                setOptionsEmbalagem([]);

                return;
            }

            // 3) Carrega embalagens do produto
            const opts = await embalagensDoProduto({
                empresaIds,
                produtoId,
            });

            // 4) Embalagem visual já presente na linha
            if (row?.Embalagem) {
                setEmbalagem(row.Embalagem);

                return;
            }

            if (embalagemId) {
                const options = Array.isArray(opts) ? opts : [];

                const found =
                    options.find((option) => Number(option?.id) === Number(embalagemId)) ||
                    options.find((option) => Number(option?.value) === Number(embalagemId));

                if (found) {
                    setEmbalagem({
                        id: found.id ?? found.value,

                        descricao: found.descricao ?? found.label ?? found.text ?? '',
                    });

                    return;
                }

                try {
                    const embResp = await EmbalagemAPI.retornaEmbalagemById(embalagemId);

                    setEmbalagem(embResp ?? {});

                    return;
                } catch (error) {
                    console.error('Erro ao carregar embalagem do item:', error);
                }
            }

            setEmbalagem({});
        },
        [
            empresaIds,
            profile,
            casaDecimal,

            dados?.id,
            dados?.empresaId,
            dados?.almoxarifadoId,

            dados?.custo,
            dados?.custoFilial,
            aplicarPrecoIndexado,

            natureza,
            operacao?.conferencia,

            produtoByIdOrUuid,
            embalagensDoProduto,
            EmbalagemAPI,
        ]
    );
    const aplicarMrtLocal = useCallback(
        (lista = []) => {
            let result = Array.isArray(lista) ? [...lista] : [];

            const filters = mrt?.columnFilters ?? [];
            const global = (mrt?.globalFilter ?? '')?.toLowerCase();
            const sorting = mrt?.sorting ?? [];
            const pageIndex = mrt?.pagination?.pageIndex ?? 0;
            const pageSize = mrt?.pagination?.pageSize ?? 5;

            if (filters.length) {
                result = result.filter((row) => {
                    return filters.every((filter) => {
                        const value = filter.value;
                        const id = filter.id;

                        const rowValue = id.split('.').reduce((acc, key) => acc?.[key], row);

                        return String(rowValue ?? '')
                            .toLowerCase()
                            .includes(String(value ?? '')?.toLowerCase());
                    });
                });
            }

            if (global) {
                result = result.filter((row) => JSON.stringify(row)?.toLowerCase().includes(global));
            }

            if (sorting.length) {
                const sort = sorting[0];

                result.sort((a, b) => {
                    const aValue = sort.id.split('.').reduce((acc, key) => acc?.[key], a);

                    const bValue = sort.id.split('.').reduce((acc, key) => acc?.[key], b);

                    const compare = String(aValue ?? '').localeCompare(String(bValue ?? ''), 'pt-BR', { numeric: true });

                    return sort.desc ? -compare : compare;
                });
            }

            const total = result.length;
            const start = pageIndex * pageSize;
            const page = result.slice(start, start + pageSize);

            setViewItensProdutos(page);
            setRowCountProduto(total);
        },
        [mrt?.columnFilters, mrt?.globalFilter, mrt?.sorting, mrt?.pagination?.pageIndex, mrt?.pagination?.pageSize]
    );

    const loadFromTabelaPreco = useCallback(
        async (tabelaPrecoId) => {
            if (!tabelaPrecoId) {
                commitList([]);
                clear({ keepTabelaPrecoId: false });
                return null;
            }

            const resultado = await MovimentoAPI.retornaTabelaPreco(tabelaPrecoId);

            if (!resultado?.tabelaPrecoItens) {
                commitList([]);
                return null;
            }

            const empresa = resultado.Empresa ?? {};
            const base = resultado.tabelaPrecoItens;

            const objTabelaPreco = {
                id: resultado?.id,
                descricao: resultado?.descricao,
                descontoGlobal: resultado?.descontoGlobal,
                percentualMaximoAcrescimo: resultado.percentualMaximoAcrescimo,
            };
            let itensPedidoParaMerge = itensProdutos;

            if (dados?.id) {
                const response = await MovimentoAPI.listarProdutosPedido({
                    start: 0,
                    size: 9999,
                    filters: JSON.stringify([]),
                    filtersMode: JSON.stringify({}),
                    globalFilter: '',
                    sorting: JSON.stringify([]),
                    movimentoUuid: dados.uuid,
                    operacaoId: dados.operacaoId,
                    empresaId: dados.empresaId,
                    movimentoId: dados.id,
                });

                itensPedidoParaMerge = response?.data?.data ?? [];
            }

            const merged = base.map((tpItem) => {
                const uuidTabela = tpItem?.Produto?.uuid;

                const existente =
                    itensPedidoParaMerge.find((i) => i.Produto?.uuid === uuidTabela) ||
                    itensPedidoParaMerge.find((i) => i.produtoId === tpItem.produtoId) ||
                    null;

                const fator = Number(existente?.fator ?? tpItem.fator ?? 1);
                const precoUnitario = Number(tpItem.precoUnitario ?? 0);
                const preco = Number(tpItem.preco ?? 0);

                const quantidade =
                    existente?.quantidade != null
                        ? Number(existente.quantidade)
                        : tpItem.quantidade != null
                          ? Number(tpItem.quantidade)
                          : '';

                const descontoPercentual =
                    existente?.descontoPercentual != null
                        ? Number(existente.descontoPercentual)
                        : tpItem.descontoPercentual != null
                          ? Number(tpItem.descontoPercentual)
                          : 0;

                const valorUnitario = existente?.valorUnitario != null ? Number(existente.valorUnitario) : preco;
                const precoNegociado = existente?.precoNegociado != null ? Number(existente.precoNegociado) : preco;
                const precoPorUnidade = existente?.precoPorUnidade != null ? Number(existente.precoPorUnidade) : precoUnitario;

                const valorTotalExistente =
                    existente?.valorTotal != null ? Number(existente.valorTotal) : precoNegociado * (Number(quantidade) || 0);

                return {
                    ...tpItem,
                    pedido: existente ? !!existente.pedido : false,
                    id: existente?.id ?? null,
                    uuid: existente?.uuid ?? null,
                    movimentoId: existente?.movimentoId ?? dados?.id ?? null,
                    uuidTabela,
                    embalagemIdTabela: tpItem.embalagemId,
                    fator: fator.toFixed(2),
                    precoUnitario: precoUnitario.toFixed(2),
                    precoBase: Number(existente?.precoBase ?? tpItem.preco ?? 0).toFixed(2),
                    preco: Number(existente?.preco ?? tpItem.preco ?? 0).toFixed(2),
                    precoNegociado: Number(precoNegociado).toFixed(2),
                    precoPorUnidade: Number(precoPorUnidade).toFixed(2),
                    quantidade: !quantidade
                        ? Number(profile?.parametros?.itens?.quantidadePadrao).toFixed(casaDecimal)
                        : Number(quantidade).toFixed(casaDecimal),
                    valorUnitario: Number(valorUnitario).toFixed(2),
                    descontoPermitidoTabela: tpItem?.desconto ?? 0,
                    descontoPercentual: Number(descontoPercentual).toFixed(2),
                    descontoValor:
                        existente?.descontoValor != null
                            ? Number(existente.descontoValor).toFixed(2)
                            : tpItem.descontoValor != null
                              ? Number(tpItem.descontoValor).toFixed(2)
                              : '0.00',
                    valorTotal: Number(valorTotalExistente).toFixed(2),
                    embalagemId: existente?.embalagemId ?? tpItem.embalagemId,
                    Embalagem: existente?.Embalagem ?? tpItem.Embalagem,
                    Produto: existente?.Produto ?? tpItem.Produto,
                    Empresa: empresa,
                    tabelaPreco: existente?.tabelaPreco ?? objTabelaPreco,
                };
            });

            commitList(merged);
            aplicarMrtLocal(merged);
            setDraft((prev) => ({
                ...prev,
                tabelaPrecoId,
            }));

            return merged;
        },
        [MovimentoAPI, itensProdutos, commitList, clear, dados?.id, casaDecimal, profile, aplicarMrtLocal]
    );

    const save = useCallback(
        async (overrideDraft = null) => {
            const d0 = overrideDraft || draft;
            const pedido = resolvePedidoByFuncao(d0?.funcao, d0?.pedido);

            const movimentoId = d0?.movimentoId ?? dados?.id ?? null;
            const empresaId = d0?.empresaId ?? dados?.empresaId ?? null;
            const almoxarifadoId = d0?.almoxarifadoId ?? dados?.almoxarifadoId ?? null;

            const d = {
                ...d0,
                pedido,
                movimentoId,
                empresaId,
                almoxarifadoId,
            };

            if (!d?.almoxarifadoId && profile?.parametros?.obrigaAlmoxarifado) {
                setFlashMessage('Almoxarifado não selecionado!', 'warning');
                return { ok: false, blocked: true };
            }

            if (dados?.uuid) {
                try {
                    const data =
                        d.funcao?.toLowerCase() === 'cancelar'
                            ? await MovimentoAPI.deletarProduto(d)
                            : await MovimentoAPI.managerProduto(d);

                    setFlashMessage(data?.message || 'Item processado', data?.type || 'success');
                    clear({ keepTabelaPrecoId: true });

                    return { ok: true, remote: true };
                } catch (error) {
                    console.error(error);
                    if (error?.response?.status === 422) {
                        setFlashMessage('Erro de validação: ' + (error.response.data?.message || ''), 'error');
                    } else {
                        setFlashMessage('Ocorreu um erro ao processar a solicitação.', 'error');
                    }
                    return { ok: false, error };
                }
            }

            const updated = upsertLocal(itensProdutos, d);
            commitList(updated);
            clear({ keepTabelaPrecoId: true });

            return { ok: true, remote: false };
        },
        [
            draft,
            dados?.uuid,
            dados?.id,
            dados?.empresaId,
            dados?.almoxarifadoId,
            itensProdutos,
            MovimentoAPI,
            setFlashMessage,
            upsertLocal,
            commitList,
            clear,
            profile,
        ]
    );

    const setRemote = useCallback(
        (lista, totalRowCount) => {
            const safe = Array.isArray(lista) ? lista : [];
            setItensProdutos(safe);
            setViewItensProdutos(null);

            const total = Number(totalRowCount);
            setRowCountProduto(Number.isFinite(total) ? total : safe.length);

            mergeDadosMovimentoItems(safe);
        },
        [mergeDadosMovimentoItems]
    );

    const resolveItensBoot = useCallback(() => {
        return profile?.uiTabs?.itens?.boot || profile?.parametros?.itens?.boot || null;
    }, [profile]);

    const refresh = useCallback(async () => {
        if (!dados?.id || !mrt) return;

        const tabelaPrecoId = draft?.tabelaPrecoId || dados?.tabelaPrecoId || null;

        /**
         * MODO TABELA:
         * Se a operação trabalha com tabela como lista,
         * não podemos chamar listarProdutosPedido,
         * pois isso sobrescreve a lista mesclada.
         */
        if (modoTabelaPreco && tabelaPrecoId) {
            return;
        }

        try {
            setIsError(false);

            if ((itensProdutos?.length ?? 0) > 0) {
                setIsRefetching(true);
            } else {
                setIsLoading(true);
            }

            const response = await MovimentoAPI.listarProdutosPedido({
                start: (mrt.pagination?.pageIndex ?? 0) * (mrt.pagination?.pageSize ?? 5),
                size: mrt.pagination?.pageSize ?? 5,
                filters: JSON.stringify(mrt.columnFilters ?? []),
                filtersMode: JSON.stringify(mrt.columnFilterFns ?? []),
                globalFilter: mrt.globalFilter ?? '',
                sorting: JSON.stringify(mrt.sorting ?? []),
                movimentoUuid: dados.uuid,
                operacaoId: dados.operacaoId,
                empresaId: dados.empresaId,
                movimentoId: dados.id,
            });

            setRemote(response?.data?.data ?? [], response?.data?.meta?.totalRowCount ?? 0);
        } catch (error) {
            console.error(error);
            setIsError(true);
        } finally {
            setIsLoading(false);
            setIsRefetching(false);
        }
    }, [
        dados?.id,
        dados?.uuid,
        dados?.operacaoId,
        dados?.empresaId,
        dados?.tabelaPrecoId,
        mrt?.pagination?.pageIndex,
        mrt?.pagination?.pageSize,
        mrt?.columnFilters,
        mrt?.columnFilterFns,
        mrt?.globalFilter,
        mrt?.sorting,
        draft?.tabelaPrecoId,
        operacaoParametros?.formTabelaOrProduto,
        loadFromTabelaPreco,
        MovimentoAPI,
        setRemote,
        itensProdutos?.length,
    ]);
    const recalcularCustos = useCallback(async () => {
        const movimentoId = Number(dados?.id);

        if (!Number.isInteger(movimentoId) || movimentoId <= 0) {
            setFlashMessage('Grave o Movimento antes de recalcular os custos.', 'warning');

            return {
                ok: false,
            };
        }

        const response = await MovimentoAPI.recalcularCustos(movimentoId);

        if (response?.type !== 'success') {
            setFlashMessage(response?.message || 'Não foi possível recalcular os custos.', response?.type || 'error', 8);

            return {
                ok: false,
                response,
            };
        }

        /*
         * Recarrega os itens diretamente do banco após o COMMIT.
         */
        await refresh();

        setFlashMessage(response.message || 'Custos recalculados com sucesso.', 'success');

        return {
            ok: true,
            data: response?.data,
        };
    }, [dados?.id, MovimentoAPI, refresh, setFlashMessage]);
    const tabelaPrecoIdAtual = draft?.tabelaPrecoId || dados?.tabelaPrecoId || null;

    useMRTListController({
        mrt,
        contextKey: `${operacao?.sigla || 'default'}-${dados?.id || 'novo'}-itens-${tabelaPrecoIdAtual || 'sem-tabela'}`,
        resolveBoot: resolveItensBoot,
        fetchData: refresh,
        contextReady: !!mrt,
        bootMode: 'simple',
    });

    const change = useCallback(
        async (e) => {
            const { name, value, checked, type } = e.target;
            // helper local pra pegar id do datalist (mantive seu padrão)
            const getIdByValue = (v) => document.querySelector(`datalist option[value="${v}"]`)?.dataset.id || null;

            if (name.startsWith('Produto.')) {
                let selectedId = null;

                if (operacaoParametros.formTabelaOrProduto) return;

                const campo = name.split('.')[1];
                const novoTexto = value;

                setProduto((prev) => ({ ...prev, [campo]: novoTexto }));

                let result = [];

                if (profile?.parametros?.itens?.digitaDatalistProduto) {
                    const filtraPorDiaHora = profile?.parametros?.itens?.datalistProdutoFiltraPorDiaHora === true;

                    result = await consultaDatalistProduto({
                        empresaId: draft.empresaId,
                        tabelaPrecoId: draft.tabelaPrecoId,
                        filtro: novoTexto,
                        filtraPorDiaHora,
                        operacaoParametros,
                    });
                } else {
                    result = await DataListAPI.dataListProduto({
                        empresaId: dados?.empresaId,
                        tabela: 'Produto',
                        produtoFiltro: profile.filtroProduto ? produtoFiltro : {},
                        filtro: novoTexto,
                    });
                }

                const options = Array.isArray(result) ? result : [];
                setOptionsProdutos(options);

                const opt = options.find((o) => {
                    const txt = (o?.descricao ?? o?.label ?? o?.value ?? '').toString().trim();
                    return txt === novoTexto.trim();
                });

                selectedId = opt?.id ?? opt?.produtoId ?? null;

                if (!selectedId) {
                    setDraft((prev) => ({ ...prev, Produto: { ...(prev.Produto || {}), [campo]: novoTexto } }));
                    return;
                }

                const [embOptions, produto] = await Promise.all([
                    embalagensDoProduto({ empresaIds, produtoId: selectedId }),
                    produtoByIdOrUuid({ id: selectedId }),
                ]);

                const embList = Array.isArray(embOptions) ? embOptions : [];
                const prevEmbId = draft?.embalagemId;
                const prevAindaServe = prevEmbId && embList.some((e) => Number(e?.id) === Number(prevEmbId));
                const embalagemIdFinal = prevAindaServe ? prevEmbId : (embList[0]?.id ?? null);
                const embalagemSelecionada = embList.find((e) => Number(e?.id) === Number(embalagemIdFinal)) || {};

                setDraft((prev) => ({
                    ...prev,
                    produtoId: selectedId,
                    embalagemId: embalagemIdFinal,
                    Produto: produto?.data,
                    Embalagem: embalagemSelecionada,
                    producaoMinima: produto?.data?.producaoMinima || '',
                }));

                if (profile?.parametros?.itens?.usaTabelaPreco && draft?.tabelaPrecoId) {
                    await obterItemTabelaPrecoByItemId({
                        tabelaPrecoId: draft.tabelaPrecoId,
                        produtoId: selectedId,
                    });
                }

                return;
            }
            if (name.startsWith('parceiroItem.')) {
                const result = await handleParceiroLookup(value, {
                    empresaId: dados?.empresaId,
                    consultaFn: consultaDatalist,
                    setOptions: setOptionsParceiroItem,
                    setTextState: setParceiroItem,
                    getIdByValue,
                    condicao: natureza,
                    setTargetState: setDraft,
                    targetKey: 'pessoaId',
                    objectKey: 'parceiroItem',
                });
                return;
            }
            if (name.startsWith('colaboradorItem.')) {
                const d = await handleColaboradorLookup(value, {
                    empresaId: dados?.empresaId,
                    tipoColaborador: null,
                    consultaFn: consultaDatalistColaborador,
                    setOptions: setOptionsColaboradorItem,
                    setColaboradorState: setColaboradorItem,
                    getIdByValue,
                    setTargetState: setDraft,
                    targetKey: 'colaboradorId',
                    objectKey: 'colaboradorItem',
                    obrigaPlantao: operacaoParametros.plantao,
                    dataPlantao,
                });

                return;
            }
            if (!name.startsWith('novoProduto.')) return;

            const campo = name.split('.')[1];
            const campoNumerico = [
                'quantidade',
                'precoBase',
                'valorUnitario',
                'precoNegociado',
                'precoPorUnidade',
                'descontoPercentual',
                'descontoValor',
                'valorTotal',
            ];
            const campoNumerico3 = ['quantidade'];

            if (campo === 'tabelaPrecoId') {
                setDraft((prev) => ({ ...prev, tabelaPrecoId: value }));
                return;
            }

            if (campo === 'embalagemId') {
                const flags = { custo: !!dados?.custo, custoFilial: !!dados?.custoFilial };
                const emb = await EmbalagemAPI.retornaEmbalagemById(value);
                setEmbalagem(emb || {});

                const embalagemData = await TabelaAPI.fatorByEmbalagemId(dados?.empresaId, value);
                const fator = Number(embalagemData?.fator ?? 1);

                if (profile?.parametros?.itens?.usaTabelaPreco) {
                    const newPreco = await precoByEmbalagemId({
                        tabelaPrecoId: draft.tabelaPrecoId,
                        produtoId: draft.produtoId,
                        embalagemId: value,
                    });

                    if (newPreco?.status === 404) {
                        setFlashMessage(newPreco?.response?.data?.message, newPreco?.response?.data?.type);
                        setDraft((prev) => ({
                            ...prev,
                            [campo]: value,
                            preco: Number(0).toFixed(2),
                            precoBase: Number(0).toFixed(2),
                            valorUnitario: Number(0).toFixed(2),
                            precoNegociado: Number(0).toFixed(2),
                            precoPorUnidade: Number(0).toFixed(2),
                            descontoValor: 0,
                            valorTotal: Number(0).toFixed(2),
                            quantidade: Number(profile?.parametros?.itens?.quantidadePadrao).toFixed(casaDecimal),
                        }));
                        return;
                    }

                    const quantidade = parseFloat(draft.quantidade || 1).toFixed(casaDecimal);
                    const precoBase = parseFloat(newPreco.preco).toFixed(2);
                    const precoNegociado = parseFloat(newPreco.preco).toFixed(2);
                    const precoPorUnidade = parseFloat(newPreco.precoUnitario).toFixed(2);

                    const valorUnitario = parseFloat(newPreco.precoUnitario).toFixed(2);
                    const descontoPercentual = parseFloat(draft.descontoPercentual || 0).toFixed(2);
                    const descontoValor = parseFloat(quantidade * valorUnitario * (descontoPercentual / 100)).toFixed(2);
                    const valorTotal = parseFloat(quantidade * newPreco.precoUnitario - descontoValor).toFixed(2);

                    const pesoBruto = parseFloat(quantidade * draft.Produto.pesoBruto).toFixed(2);
                    const pesoLiquido = parseFloat(quantidade * draft.Produto.pesoLiquido).toFixed(2);
                    const volume = Number(quantidade * draft.Produto.volume);

                    setDraft((prev) => {
                        const base = {
                            ...prev,
                            [campo]: value,
                            fator,
                            Embalagem: emb,
                            prato: newPreco.prato,
                            precoBase: Number(precoBase).toFixed(2),
                            precoNegociado: Number(precoNegociado).toFixed(2),
                            precoPorUnidade: Number(precoPorUnidade).toFixed(2),
                            valorUnitario: Number(valorUnitario).toFixed(2),
                            preco: Number(precoBase).toFixed(2),
                            descontoValor,
                            valorTotal,
                            pesoBruto,
                            pesoLiquido,
                            volume,
                            embalagemIdTabela: value,
                        };

                        return profile?.parametros?.itens?.valorUnitarioIndexado ? aplicarPrecoIndexado(base, flags) : base;
                    });
                } else {
                    setDraft((prev) => {
                        const qtd = toNumberSafe(prev.quantidade || 1);
                        const precoUnit = toNumberSafe(prev.precoUnitario);
                        const descPerc = toNumberSafe(prev.descontoPercentual);

                        const precoNegociado = toNumberSafe(prev.precoNegociado);
                        const precoPorUnidade = toNumberSafe(prev.precoPorUnidade);

                        const valorUnit = precoUnit;
                        const descVal = qtd * valorUnit * (descPerc / 100);
                        const total = qtd * precoUnit - descVal;

                        const base = {
                            ...prev,
                            embalagemId: value,
                            fator,
                            preco: precoUnit.toFixed(2),
                            valorUnitario: valorUnit.toFixed(2),
                            descontoValor: descVal.toFixed(2),
                            valorTotal: total.toFixed(2),
                            Embalagem: emb || prev.Embalagem,
                            precoNegociado,
                            precoPorUnidade,
                        };

                        return profile?.parametros?.itens?.valorUnitarioIndexado ? aplicarPrecoIndexado(base, flags) : base;
                    });
                }

                return;
            }

            if (campoNumerico.includes(campo)) {
                const valorFormatado = campoNumerico3.includes(campo)
                    ? utiliza3digitos
                        ? formatarNumeroDecimal3Digitos(value)
                        : formatarNumeroDecimal(value)
                    : formatarNumeroDecimal(value);

                setDraft((prev) => MovimentoAPI.calculaValores({ ...prev, [campo]: valorFormatado }, campo));
                return;
            }

            setDraft((prev) => ({
                ...prev,
                [campo]: type === 'checkbox' ? checked : value,
            }));
        },
        [
            MovimentoAPI,
            DataListAPI,
            TabelaAPI,
            EmbalagemAPI,
            dados?.empresaId,
            dados?.custo,
            dados?.custoFilial,
            produtoFiltro,
            utiliza3digitos,
            formatarNumeroDecimal,
            formatarNumeroDecimal3Digitos,
            profile,
            draft,
            empresaIds,
            operacaoParametros,
        ]
    );

    const obterItemTabelaPrecoByItemId = useCallback(
        async ({ tabelaPrecoId, produtoId }) => {
            if (!tabelaPrecoId || !produtoId) return;

            const result = await MovimentoAPI.retornaTabelaPrecoItem(tabelaPrecoId, produtoId);
            const pesavel = result?.Produto?.pesavel || false;

            setDraft((prev) => {
                const precoUnitario = Number(result.precoUnitario);
                const preco = Number(result.preco);

                const baseProduto = {
                    ...prev,
                    Embalagem: result.Embalagem,
                    Empresa: result.empresa,
                    Produto: result.Produto,
                    Operacao: operacao,
                    movimentoId: dados.id,
                    tabelaPrecoId,
                    embalagemId: result.embalagemId,
                    fator: result.fator,
                    precoBase: preco.toFixed(2),
                    precoNegociado: preco.toFixed(2),
                    precoPorUnidade: precoUnitario.toFixed(2),
                    preco: preco.toFixed(2),
                    precoUnitario: precoUnitario.toFixed(2),
                    valorUnitario: precoUnitario.toFixed(2),
                    prato: result.prato,
                    pesavel,
                };

                return aplicarPrecoIndexado(baseProduto, {
                    custo: dados.custo,
                    custoFilial: dados.custoFilial,
                });
            });
        },
        [aplicarPrecoIndexado, MovimentoAPI, operacao, dados?.id, dados?.custo, dados?.custoFilial]
    );

    const reaplicarPrecosDraft = useCallback(
        (flags) => {
            setDraft((prev) => aplicarPrecoIndexado(prev, flags));
        },
        [aplicarPrecoIndexado]
    );

    const reaplicarPrecosLista = useCallback(
        (flags) => {
            const listaAtualizada = Array.isArray(itensProdutos)
                ? itensProdutos.map((item) => aplicarPrecoIndexado(item, flags))
                : [];

            commitList(listaAtualizada);

            return listaAtualizada;
        },
        [itensProdutos, aplicarPrecoIndexado, commitList]
    );

    const reaplicarPrecos = useCallback(
        (flags) => {
            reaplicarPrecosDraft(flags);
            reaplicarPrecosLista(flags);
        },
        [reaplicarPrecosDraft, reaplicarPrecosLista]
    );

    // pedido padrão
    const montarItemPedidoPadrao = useCallback(
        async ({ item, pedidoPadrao }) => {
            const tabelaPrecoId =
                item?.tabelaPrecoId || pedidoPadrao?.tabelaPrecoId || dados?.tabelaPrecoId || draft?.tabelaPrecoId || null;

            const produtoId = item.produtoId;
            const quantidade = Number(item.quantidade || 0);

            const result = await MovimentoAPI.retornaTabelaPrecoItem(tabelaPrecoId, produtoId);

            const precoUnitario = Number(result?.precoUnitario || 0);
            const preco = Number(result?.preco || 0);
            const fator = Number(result?.fator || 1);

            let base = {
                ...draft,
                id: null,
                uuid: uuidv4(),

                movimentoId: dados?.id ?? null,
                empresaId: dados?.empresaId ?? null,
                almoxarifadoId: dados?.almoxarifadoId ?? null,

                tabelaPrecoId,
                produtoId,
                embalagemId: result?.embalagemId || item?.embalagemId || null,
                embalagemIdTabela: result?.embalagemId || item?.embalagemId || null,

                Produto: result?.Produto || item?.Produto || {},
                Embalagem: result?.Embalagem || item?.Embalagem || {},
                Empresa: result?.empresa || {},

                quantidade: quantidade.toFixed(casaDecimal),
                quantidadeOriginal: quantidade.toFixed(casaDecimal),

                fator,
                precoBase: preco.toFixed(2),
                preco: preco.toFixed(2),
                precoNegociado: preco.toFixed(2),
                precoPorUnidade: precoUnitario.toFixed(2),
                precoUnitario: precoUnitario.toFixed(2),
                valorUnitario: precoUnitario.toFixed(2),

                descontoPercentual: '0.00',
                descontoValor: '0.00',

                funcao: 'Adicionar',
                pedido: true,
                ativo: true,
            };

            base = aplicarPrecoIndexado(base, {
                custo: dados?.custo,
                custoFilial: dados?.custoFilial,
            });

            base = MovimentoAPI.calculaValores(base, 'quantidade');

            return normalizeDraftToItem(base);
        },
        [
            draft,
            dados?.id,
            dados?.empresaId,
            dados?.almoxarifadoId,
            dados?.tabelaPrecoId,
            dados?.custo,
            dados?.custoFilial,
            casaDecimal,
            MovimentoAPI,
            aplicarPrecoIndexado,
            normalizeDraftToItem,
        ]
    );

    const adicionarItemPedidoPadrao = useCallback(
        async ({ item, pedidoPadrao }) => {
            if (!item?.produtoId) {
                return { ok: false, message: 'Produto não informado no item do pedido padrão.' };
            }

            const tabelaPrecoId =
                item?.tabelaPrecoId || pedidoPadrao?.tabelaPrecoId || dados?.tabelaPrecoId || draft?.tabelaPrecoId || null;

            if (!tabelaPrecoId && profile?.parametros?.itens?.usaTabelaPreco) {
                setFlashMessage('Tabela de preço não informada para carregar o pedido padrão.', 'warning');
                return { ok: false };
            }

            const produtoId = item.produtoId;
            const embalagemIdPadrao = item.embalagemId || null;
            const quantidade = Number(item.quantidade || 0);

            if (quantidade <= 0) {
                return { ok: false, message: 'Quantidade inválida no item do pedido padrão.' };
            }

            try {
                const [produtoResp, embOptions] = await Promise.all([
                    produtoByIdOrUuid({ id: produtoId }),
                    embalagensDoProduto({ empresaIds, produtoId }),
                ]);

                const produto = produtoResp?.data ?? produtoResp ?? {};
                const embalagens = Array.isArray(embOptions) ? embOptions : [];

                const embalagemSelecionada =
                    embalagens.find((emb) => Number(emb?.id) === Number(embalagemIdPadrao)) ||
                    embalagens[0] ||
                    item?.Embalagem ||
                    {};

                const embalagemId = embalagemSelecionada?.id || embalagemIdPadrao || null;

                if (!embalagemId) {
                    setFlashMessage(`Produto ${produto?.descricao || produtoId} sem embalagem.`, 'warning');
                    return { ok: false };
                }

                let draftBase = {
                    ...draft,
                    id: null,
                    uuid: null,

                    movimentoId: dados?.id ?? null,
                    empresaId: dados?.empresaId ?? null,
                    almoxarifadoId: dados?.almoxarifadoId ?? null,

                    tabelaPrecoId,
                    produtoId,
                    embalagemId,

                    Produto: produto,
                    Embalagem: embalagemSelecionada,

                    quantidade: Number(quantidade).toFixed(casaDecimal),
                    quantidadeOriginal: Number(quantidade).toFixed(casaDecimal),

                    funcao: 'Adicionar',
                    index: null,
                    pedido: true,

                    descontoPercentual: '0.00',
                    descontoValor: '0.00',
                };

                if (profile?.parametros?.itens?.usaTabelaPreco) {
                    const result = await MovimentoAPI.retornaTabelaPrecoItem(tabelaPrecoId, produtoId);

                    if (!result) {
                        setFlashMessage(
                            `Produto ${produto?.descricao || produtoId} não encontrado na tabela de preço.`,
                            'warning'
                        );
                        return { ok: false };
                    }

                    const precoUnitario = Number(result.precoUnitario || 0);
                    const preco = Number(result.preco || 0);
                    const fator = Number(result.fator || 1);
                    const pesavel = result?.Produto?.pesavel || false;

                    draftBase = {
                        ...draftBase,
                        Embalagem: result.Embalagem || embalagemSelecionada,
                        Empresa: result.empresa || draftBase.Empresa,
                        Produto: result.Produto || produto,
                        Operacao: operacao,

                        embalagemId: result.embalagemId || embalagemId,
                        embalagemIdTabela: result.embalagemId || embalagemId,

                        fator,
                        precoBase: preco.toFixed(2),
                        preco: preco.toFixed(2),
                        precoUnitario: precoUnitario.toFixed(2),
                        valorUnitario: precoUnitario.toFixed(2),
                        precoNegociado: preco.toFixed(2),
                        precoPorUnidade: precoUnitario.toFixed(2),
                        prato: result.prato,
                        pesavel,
                    };

                    draftBase = aplicarPrecoIndexado(draftBase, {
                        custo: dados?.custo,
                        custoFilial: dados?.custoFilial,
                    });

                    draftBase = MovimentoAPI.calculaValores(draftBase, 'quantidade');
                } else {
                    draftBase = MovimentoAPI.calculaValores(draftBase, 'quantidade');
                }

                return await save(draftBase);
            } catch (error) {
                console.error('Erro ao adicionar item do pedido padrão:', error);
                setFlashMessage('Erro ao adicionar item do pedido padrão.', 'error');

                return { ok: false, error };
            }
        },
        [
            draft,
            dados?.id,
            dados?.empresaId,
            dados?.almoxarifadoId,
            dados?.tabelaPrecoId,
            dados?.custo,
            dados?.custoFilial,
            empresaIds,
            casaDecimal,
            profile,
            operacao,
            produtoByIdOrUuid,
            embalagensDoProduto,
            aplicarPrecoIndexado,
            MovimentoAPI,
            setFlashMessage,
            save,
        ]
    );
    const carregarPedidoPadraoNoMovimento = useCallback(
        async (pedidoPadrao, options = {}) => {
            const itens = pedidoPadrao?.Itens || [];

            if (!itens.length) {
                setFlashMessage('Pedido padrão sem itens.', 'warning');
                return { ok: false };
            }

            const novosItens = [];

            for (const item of itens) {
                try {
                    const novoItem = await montarItemPedidoPadrao({
                        item,
                        pedidoPadrao,
                    });

                    novosItens.push(novoItem);
                } catch (error) {
                    console.error('Erro ao montar item do pedido padrão:', error);
                }
            }

            if (!novosItens.length) {
                setFlashMessage('Nenhum item foi carregado do pedido padrão.', 'warning');
                return { ok: false };
            }

            const listaAtual = options?.substituir === true ? [] : Array.isArray(itensProdutos) ? itensProdutos : [];

            const listaFinal = [...listaAtual];

            for (const novoItem of novosItens) {
                const indexExistente = listaFinal.findIndex(
                    (item) =>
                        Number(item.produtoId) === Number(novoItem.produtoId) &&
                        Number(item.embalagemId) === Number(novoItem.embalagemId)
                );

                if (indexExistente >= 0) {
                    listaFinal[indexExistente] = {
                        ...listaFinal[indexExistente],
                        quantidade: novoItem.quantidade,
                        quantidadeOriginal: novoItem.quantidadeOriginal,
                        valorTotal: novoItem.valorTotal,
                        pedido: true,
                    };
                } else {
                    listaFinal.push(novoItem);
                }
            }

            commitList(listaFinal);

            setFlashMessage(`Pedido padrão carregado: ${novosItens.length} item(ns).`, 'success');

            return {
                ok: true,
                adicionados: novosItens.length,
            };
        },
        [itensProdutos, montarItemPedidoPadrao, commitList, setFlashMessage]
    );

    // =========================================================
    // CONTRATO NOVO
    // =========================================================
    const main = {
        state: mainState,
        setState: setMainState,
    };

    const draftContract = {
        value: draftValue,
        setValue: setDraftValue,
        clear,
        selectRow,
    };

    const list = {
        items: itensProdutos,
        rowCount: rowCountProduto,
        setItems: commitList,
        setRowCount: setRowCountProduto,
        view: viewItensProdutos,
        setView: setViewItensProdutos,
    };

    const mrtContract = {
        raw: mrt,
        state: {
            columnFilterFns: mrt?.columnFilterFns ?? {},
            columnFilters: mrt?.columnFilters ?? [],
            globalFilter: mrt?.globalFilter ?? '',
            pagination: mrt?.pagination ?? { pageIndex: 0, pageSize: 5 },
            sorting: mrt?.sorting ?? [],
        },
        setters: {
            setColumnFilterFnsMode: mrt?.setColumnFilterFnsMode || mrt?.setColumnFiltersMode,
            setColumnFilters: mrt?.setColumnFilters,
            setGlobalFilter: mrt?.setGlobalFilter,
            setPagination: mrt?.setPagination,
            setSorting: mrt?.setSorting,
        },
        bind: {
            onColumnFilterFnsChange: mrt?.setColumnFilterFnsMode || mrt?.setColumnFiltersMode,
            onColumnFiltersChange: mrt?.setColumnFilters,
            onGlobalFilterChange: mrt?.setGlobalFilter,
            onPaginationChange: mrt?.setPagination,
            onSortingChange: mrt?.setSorting,
        },
        flags: {
            isLoading,
            isRefetching,
            isError,
            setIsLoading,
            setIsRefetching,
            setIsError,
        },
    };

    const services = {
        mergeDadosMovimentoItems,
        commitList,
        normalizeDraftToItem,
        getKeyValue,
        upsertLocal,
        embalagensDoProduto,
        produtoByIdOrUuid,
        consultaDatalistProduto,
        consultaEmbalagemByProdutoId,
        precoByEmbalagemId,
        aplicarPrecoIndexado,
        obterMovimentoItem,
        obterItemTabelaPrecoByItemId,
        reaplicarPrecos,
        reaplicarPrecosDraft,
        reaplicarPrecosLista,
    };

    const actions = {
        change,
        focus: async () => {},
        blur: async () => {},

        refresh: async () => {
            return await refreshLock.run(async () => {
                return await refresh();
            });
        },

        recalculateCosts: async () => {
            return await costRecalculationLock.run(async () => {
                return await recalcularCustos();
            });
        },

        submitDraft: async () => {
            return await saveLock.run(async () => {
                const result = await save();

                if (result?.ok && dados?.id) {
                    const tabelaPrecoId = draft?.tabelaPrecoId || dados?.tabelaPrecoId || null;

                    if (modoTabelaPreco && tabelaPrecoId) {
                        await loadFromTabelaPreco(tabelaPrecoId);
                    } else {
                        await refresh();
                    }
                }

                return result;
            });
        },
        remove: async (item) => {
            return await saveLock.run(async () => {
                const result = await save({ ...item, funcao: 'Cancelar' });

                if (result?.ok && dados?.id) {
                    await refresh();
                }

                return result;
            });
        },

        setRemote,
        syncFromFormData: (lista, total) => {
            setRemote(lista, total);
        },

        selectRow,
        save,
        clear,
        loadFromTabelaPreco,
        setView: (lista) => setViewItensProdutos(Array.isArray(lista) ? lista : []),
        clearView: () => setViewItensProdutos(null),

        obterMovimentoItem,
        obterItemTabelaPrecoByItemId,
        aplicarPrecoIndexado,
        reaplicarPrecos,
        reaplicarPrecosDraft,
        reaplicarPrecosLista,
        commitList,
        // pedido padrão
        adicionarItemPedidoPadrao,
        carregarPedidoPadraoNoMovimento,
    };

    // =========================================================
    // COMPAT TEMPORÁRIA
    // =========================================================
    const state = {
        draft,
        setDraft,
        itensProdutos,
        setItensProdutos: commitList,
        rowCountProduto,
        setRowCountProduto,
        Produto,
        setProduto,
        Embalagem,
        setEmbalagem,
        OptionsProdutos,
        setOptionsProdutos,
        OptionsEmbalagem,
        setOptionsEmbalagem,
        viewItensProdutos,
        setViewItensProdutos,

        parceiroItem,
        setParceiroItem,
        optionsParceiroItem,
        setOptionsParceiroItem,

        colaboradorItem,
        setColaboradorItem,
        optionsColaboradorItem,
        setOptionsColaboradorItem,

        modoTabelaPreco,
    };

    const display = useMemo(
        () => ({
            preco: formatarValorMonetario(draft?.preco),
            precoUnitario: formatarValorMonetario(draft?.precoUnitario),
        }),
        [draft?.preco, draft?.precoUnitario]
    );

    return {
        key: 'itens',

        main,
        draft: draftContract,
        list,
        mrt: mrtContract,
        flags,
        actions,
        services,
        policies,

        // compat temporária
        state,

        display,
    };
}
